import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Theme from 'components/common/Theme/Theme'
import Text, { H2 } from 'components/common/Text/Text'
import Button from 'components/common/Button/Button'
import Media from 'components/common/Media'
import Mixin from 'components/common/Mixin'
import Icon from 'components/common/Icon/Icon'
import Link from 'components/common/Link/Link'
import Popover from 'components/common/Popover'
import { filterProps } from 'helpers/utils'
import { QUICK_APPOINTMENT_PAGE } from 'constant'
import OrSeparator from 'components/common/OrSeparator/OrSeparator'

const LocationItem = ({
  handleClose,
  selected,
  clickHandler,
  address,
  street,
  distanceRadius,
  appointmentEnable,
  phoneNumbers = [],
  selectStoreHandler,
  storeDetailsUrl,
  isSaved,
  hasCustomHandler,
  handlePhoneLinkClick,
  enhancedStoreResultsEnabled,
  handleShopTires
}) =>
  <Wrapper tabIndex="0" data-at-storelocator-store selected={ selected && !hasCustomHandler } onFocus={ clickHandler } onClick={ clickHandler }>
    {
      isSaved &&
        <Fragment>
          {
            enhancedStoreResultsEnabled ?
              <div>
                <StoreSelected>Store Selected</StoreSelected>
                <Checked alt="" asset="check-circle" />
              </div> :
              <Checked alt="" asset="check-circle" />
          }
        </Fragment>
    }
    <TextWrapper mobile>
      <H2 data-at-store-address noMargin>
        <Text bolder size={ 18 }>{ street }</Text><br />
        <Text bolder size={ 18 }>{ address }</Text>
      </H2>
      <Media.Mobile>
        <TextMile data-at-store-distance size="12">
          <Icon data-at-store-marker-icon alt="" asset="location-red" margin="-2px 4px 0 0" />
          <span data-at-store-miles>{ distanceRadius } Miles</span>
        </TextMile>
      </Media.Mobile>
    </TextWrapper>
    <TextWrapper enhancedStoreResultsEnabled={ isSaved && enhancedStoreResultsEnabled } flexMobile>
      <Media.Desktop>
        <TextMile data-at-store-distance size="12">
          <Icon data-at-store-marker-icon alt="" asset="location-red" margin="-2px 4px 0 0" />
          <span data-at-store-miles>{ distanceRadius } Miles</span>
        </TextMile>
        {
          appointmentEnable &&
          <StyledPopover
            asset="calendar-red"
            alternative="calendar-red"
            ariaLabel="Online appointments enabled"
            innerProps={{
              popoverIconProps: {
                'data-at-store-calendar-icon': true
              }
            }}>
            This store accepts online appointment requests.
          </StyledPopover>
        }
      </Media.Desktop>
      <StoreDetails
        data-at-storelocator-store-details-link
        to={ storeDetailsUrl }
        enhancedStoreResultsEnabled={ isSaved && enhancedStoreResultsEnabled }
        onClick={ handleClose }>
        Store details
        <Media.ScreenReader>for { street }</Media.ScreenReader>
      </StoreDetails>
      <Media.Mobile>
        {
          appointmentEnable &&
          <StyledPopover
            asset="calendar-red"
            alternative="calendar-red"
            ariaLabel="Online appointments enabled"
            innerProps={{
              popoverIconProps: {
                'data-at-store-calendar-icon': true
              }
            }}>
            This store accepts online appointment requests.
          </StyledPopover>
        }
      </Media.Mobile>
    </TextWrapper>
    <ButtonWrapper>
      {
        enhancedStoreResultsEnabled ?
          <Fragment>
            {
              !isSaved &&
                <SelectStoreButton
                  stacked="mobile"
                  full="mobile"
                  narrow
                  data-at-select-store-button
                  disabled={ isSaved && !hasCustomHandler }
                  onClick={ selectStoreHandler }>
                  select store
                  <Media.ScreenReader>at { street }</Media.ScreenReader>
                </SelectStoreButton>
            }
          </Fragment> :
          <SelectStoreButton
            stacked="mobile"
            full="mobile"
            data-at-select-store-button
            narrow
            disabled={ isSaved && !hasCustomHandler }
            onClick={ selectStoreHandler }>
            select store
            <Media.ScreenReader>at { street }</Media.ScreenReader>
          </SelectStoreButton>
      }
    </ButtonWrapper>
    <OrSeparator />
    <CallStoreText>Call store to get help or schedule appointment.</CallStoreText>
    {
      phoneNumbers
        .map((phone, index) =>
          <React.Fragment key={ index }>
            <Media.Desktop>
              <PhoneText>
                { phone }
              </PhoneText>
            </Media.Desktop>
            <Media.Mobile>
              <PhoneButtonLink href={ `tel:+1${phone}` }>
                <Icon asset="phone-red" height={ 13 } width={ 13 } />
                <PhoneRed>{ phone }</PhoneRed>
              </PhoneButtonLink>
            </Media.Mobile>
          </React.Fragment>
        )
    }
    {
      isSaved && enhancedStoreResultsEnabled &&
        <EnhancedStoreResultsWrapper>
          <ShopTiresButton
            full
            onClick={ handleShopTires }>
            Shop Tires
          </ShopTiresButton>
          <QuickAppointmentCTA
            button
            full
            to={ QUICK_APPOINTMENT_PAGE }>
            Quick Appointment
          </QuickAppointmentCTA>
        </EnhancedStoreResultsWrapper>
    }
  </Wrapper>

export default LocationItem

LocationItem.propTypes = {
  address: PropTypes.string,
  appointmentEnable: PropTypes.bool,
  clickHandler: PropTypes.func,
  distanceRadius: PropTypes.number,
  enhancedStoreResultsEnabled: PropTypes.bool,
  handleClose: PropTypes.func,
  handlePhoneLinkClick: PropTypes.func,
  handleShopTires: PropTypes.func,
  hasCustomHandler: PropTypes.bool,
  isSaved: PropTypes.bool,
  phoneNumbers: PropTypes.arrayOf(PropTypes.string),
  selected: PropTypes.bool,
  selectStoreHandler: PropTypes.func,
  storeDetailsUrl: PropTypes.string,
  street: PropTypes.string
}

const Wrapper = styled.li`
  position: relative;
  cursor: pointer;
  ${Mixin.responsive('padding', '20px 0 20px 20px', '20px 20px 5px 20px')};
  ${Mixin.responsive('margin-bottom', '0', '12px')};
  ${Mixin.responsive('border', '0', `1px solid ${Theme.colors.light_grey}`)};
  ${Media.desktop`
    & + &::before {
      content:'';
      position: absolute;
      width: 86%;
      height: 1px;
      background-color: ${Theme.colors.light_grey};
      top: 0;
    }
  `}

  ${props => props.selected && css`
    && {
      border: 2px solid ${Theme.colors.black};

      ${Media.desktop`
        &::before {
          display: none;
        }
      `};
    }
  `}
`

const Checked = styled(Icon)`
  position: absolute;
  top: 10px;
  right: 10px;
`

const TextWrapper = styled.div`
  padding-right: 20%;
  margin-bottom: 20px;
  ${props => props.enhancedStoreResultsEnabled && css`
    margin-bottom: 10px;
  `}

  ${Media.mobile`
    ${props => props.mobile && css`
      display: flex;
      justify-content: space-between;
      padding-right: 0;
    `}

    ${props => props.flexMobile && css`
      display: flex;
    `}
  `}
`

const ButtonWrapper = styled.div`
  & ${Button} {
    padding: 7px 16px 8px;
  }
`

const StoreDetails = styled(props => <Link { ...filterProps(props, ['enhancedStoreResultsEnabled']) } />)`
  ${Mixin.responsive('display', 'inline-block', 'block')};
  ${Mixin.responsive('padding', '14px 0 14px 5px', '0')};
  ${Mixin.font.GothamMedium(12, 500)}
  ${Mixin.responsive('margin', '0', '0')};
  ${props => props.enhancedStoreResultsEnabled && css`
    margin: 0 0 5px 0;
    padding: 0 0 14px 0;
  `}
  line-height: 12px;
  text-align: center;
  text-decoration: underline;
  ${Media.mobile`
    text-align: start;
  `}
`

const SelectStoreButton = styled(Button)`
  width: 93%;
`

const TextMile = styled(Text)`
  vertical-align: middle;
`

const StoreSelected = styled.span`
  position: relative;
  bottom: 10px;
  text-transform: uppercase;
  color: ${Theme.colors.green};
  font-weight: 700;
`

const StyledPopover = styled(Popover)`
  margin: -2px 0 0 20px;

  && img {
    height: 20px;
  }
`

const EnhancedStoreResultsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const ShopTiresButton = styled(Button)`
  margin-left: -10px;
`

const QuickAppointmentCTA = styled(Link)`
  text-transform: uppercase;
  margin: 20px 0 0 -10px;
`

const CallStoreText = styled.p`
  margin: 0 16px 15px 0;
  text-align: center;
  font-size: 12px;
`

const PhoneText = styled.p`
  width: 100%;
  margin: 0;
  text-align: center;
  font-weight: 700;
  font-size: 16px;
`

const PhoneRed = styled.p`
  margin: 0 0 0 5px;
  color: ${Theme.colors.bright_red_new};
`

const PhoneButtonLink = styled.a`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 8px 0;
  margin: 0 0 15px;
  text-decoration: none;
  text-align: center;
  border: 2px solid ${Theme.colors.bright_red_new};
`
